/**
 * Team Edit Route
 *
 * Edit an existing team using TeamForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTeam } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { TeamForm } from '../../components/teams';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import { updateTeam, type UpdateTeamInput, ApiError } from '../../lib/apiClient';
import type { CreateTeamFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/teams/$teamId/edit')({
  component: TeamEditPage,
});

function TeamEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { teamId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch team data
  const { data, loading, error: fetchError } = useTeam(teamId);
  const team = data?.team;

  if (loading) {
    return (
      <div className="team-edit-page">
        <LoadingSpinner centered message="Loading team..." />
      </div>
    );
  }

  if (fetchError || !team) {
    return (
      <div className="team-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load team"
          description={fetchError?.message || 'Team not found'}
          actionLabel="Back to Teams"
          onAction={() => navigate({ to: '/teams', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateTeamFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input (only include changed fields)
      const updates: UpdateTeamInput = {
        name: formData.name,
        description: formData.description || undefined,
        type: formData.type,
        parentTeamId: formData.parentTeamId || undefined,
        leadUserId: formData.leadUserId,
        memberIds: formData.memberIds,
        responsibilities: formData.responsibilities,
        contactEmail: formData.contactEmail,
        teamsChannel: formData.teamsChannel || undefined,
      };

      await updateTeam(teamId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetTeam', 'ListTeams'],
      });

      // Navigate back to team detail page
      navigate({ to: `/teams/${teamId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/teams/${teamId}` as string });
  };

  // Map team data to form default values
  const defaultValues: Partial<CreateTeamFormData> = {
    name: team.name,
    description: team.description || undefined,
    type: team.type,
    parentTeamId: team.parentTeamId || undefined,
    leadUserId: team.leadUserId,
    memberIds: team.memberIds,
    responsibilities: team.responsibilities,
    contactEmail: team.contactEmail,
    teamsChannel: team.teamsChannel || undefined,
  };

  return (
    <div className="team-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Team</h1>
        <p className="forge-typography--body2 page-header-subtitle">Update team information</p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TeamForm
        mode="edit"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        currentTeamId={team.entityId}
      />
    </div>
  );
}
